import { z } from "zod";
import { EntityType } from "./entity";
import { WithFields } from "./fields";
import { Infrastructure, Substation } from "./infrastructure";
import { GeocodeSearchResult } from "./map";
import { Organisation } from "./organisation";

export const countries = {
  aus: "Australia",
  nz: "New Zealand",
};
export const ausStates = ["ACT", "NSW", "NT", "QLD", "SA", "TAS", "VIC", "WA"];
export const nzRegions = [
  "Auckland Region",
  "Bay of Plenty Region",
  "Canterbury Region",
  "Gisborne Region",
  "Hawke's Bay Region",
  "Manawatu-Whanganui Region",
  "Marlborough Region",
  "Nelson Region",
  "Northland Region",
  "Otago Region",
  "Southland Region",
  "Taranaki Region",
  "Tasman Region",
  "Waikato Region",
  "Wellington Region",
  "West Coast Region",
];

const STATIC_VIS_TYPES = [
  "boundaries_prj",
  "generation_prj",
  "storage_prj",
  "heatmap_prj",
  "voltage_net",
] as const;

export const V1_VIS_TYPES = [
  "mlf_prj",
  "emissions_prj",
  "production_prj",
  "vwap_prj",
];

const TIME_SERIES_IDS = [
  "mlf_prj",
  "emissions_prj",
  "production_prj",
  "vwap_prj",
  "curtailment_prj",
  "headroom_net",
  "headroompct_net",
] as const;

const ALL_VIS_TYPES = [...STATIC_VIS_TYPES, ...TIME_SERIES_IDS] as const;

export type TimeSeriesId = (typeof TIME_SERIES_IDS)[number];

export interface EntityTimeSeriesData {
  type: TimeSeriesId;
  entity_id: string;
  // shape: {year: value}
  values: Record<number, number>;
}

export interface EntityTimeSeriesDataResponse {
  min: number; // year digits e.g 2021
  max: number; // year digits e.g 2021
  data: Record<string, EntityTimeSeriesData>;
}

export interface EntityTimeSeriesNamed {
  type: string;
  entity_id: string;
  // shape: {year: {value_name, value}}
  values: Record<number, Record<string, number>>;
}

export interface ProjectGeoJsonAttributes {
  project_name: string;
  state: string;
  country: "Australia" | "New Zealand";
  id: string;
}

export interface SizeKey {
  label: string;
  units: string;
  marks: [number, number, number];
}

export interface ColorKey {
  label: string;
  units?: string;
  colors: string[];
  noData?: boolean;
  leftLabel?: string;
  rightLabel?: string;
}

export interface VisLegendSpec {
  size?: SizeKey;
  colour?: ColorKey;
  technology?: boolean;
  lineWidth?: boolean;
}

export interface MapVisOption {
  image: string;
  name: string;
  description: string;
  alt: string;
  key: VisType;
  units?: string | null;
  hidden?: (hasOperationalAddon: boolean) => boolean;
  entity_type: EntityType;
  disabled?: boolean;
  legend?: VisLegendSpec;
  // for visualisations driven by the time picker
  timeseries?: {
    // series whose values colour the visualisation
    color: string;
    // series whose values drive an animation over it, where it has one
    animation?: string;
  };
}

export type VisType = (typeof ALL_VIS_TYPES)[number];

export const visTypeSchema = z.enum(ALL_VIS_TYPES);

export const isVisType = (vis: unknown): vis is VisType =>
  visTypeSchema.safeParse(vis).success;

export const TIMESERIES_VIS_TYPES = new Set<string>(TIME_SERIES_IDS);

export const isTimeVisType = (vis: VisType) => TIMESERIES_VIS_TYPES.has(vis);

export const TIME_SERIES_FIELD_NAME: Record<TimeSeriesId, string> = {
  mlf_prj: "MLF",
  emissions_prj: "Emissions",
  production_prj: "Production",
  vwap_prj: "VWAP",
  curtailment_prj: "Curtailment",
  headroom_net: "Headroom",
  headroompct_net: "HeadroomPct",
};

const TIME_SERIES_FIELD_NAMES = new Set(Object.values(TIME_SERIES_FIELD_NAME));

export const isTimeVisName = (value: unknown): value is string =>
  typeof value === "string" && TIME_SERIES_FIELD_NAMES.has(value);

export type MvtLayerId =
  | "turbine-points"
  | "zones-point"
  | "lga-point"
  | "network-line"
  | "gas-line"
  | "network-geom"
  | "substations-geom"
  | Technology;

// ALPHA-2 ISO 3166 international standard country codes
export type CountryCode = "AU" | "NZ";

export interface DBAggregateProjectData {
  state: string;
  technology: string;
  project_status: string;
  count: number;
  mw: number;
  mwh: number;
}

export type AggregateProjectData = Record<
  ProjectStatus | Technology | string,
  number
> & {
  dimension: string;
  total: number;
};

export interface AggregatedProjectDataResponse {
  group: AggregateGroupType;
  value: AggregateValueType;
  data: AggregateProjectData[];
}

export type AggregateValueType = "count" | "mw" | "mwh";
export type AggregateGroupType = "project_status" | "technology" | "state";
export type ProjectTechnology =
  | "Battery"
  | "Biogas"
  | "Biomass"
  | "Hydro"
  | "Hydrogen"
  | "Geothermal"
  | "Offshore Wind"
  | "Onshore Wind"
  | "Pumped Hydro"
  | "Solar PV"
  | "Solar Thermal"
  | "Nuclear"
  | "Coal (Black)"
  | "Coal (Brown)"
  | "Distillate"
  | "Gas"
  | "Data Centre"
  | "Research";

export const ProjectTechnologyColors: Record<ProjectTechnology, string> = {
  Battery: "#FF3131",
  Biogas: "#39b1b7",
  Biomass: "#b8860b",
  Hydro: "#3548a0",
  Hydrogen: "#08f70a",
  Geothermal: "#967708",
  "Offshore Wind": "#10ecf8",
  "Onshore Wind": "#4db802",
  "Pumped Hydro": "#a043d9",
  "Solar PV": "#fff201",
  "Solar Thermal": "#ff7f00",
  Nuclear: "#f542ef",
  "Coal (Black)": "#000",
  "Coal (Brown)": "#290606",
  Distillate: "#e5b636",
  Gas: "#f66206",
  "Data Centre": "#b8307a",
  Research: "white",
};

export const projectTechnologyHslColors: {
  [key: string]: {
    h: number;
    s: number;
    l: number;
    css: string;
  };
} = {
  Battery: {
    h: 358,
    s: 100,
    l: 59.61,
    css: "hsl(0, 100%, 59.61%)",
  },
  Biogas: {
    h: 183,
    s: 53,
    l: 47,
    css: "hsl(183, 53%, 47%)",
  },
  Biomass: {
    h: 43,
    s: 89,
    l: 38,
    css: "hsl(43, 89%, 38%)",
  },
  Hydro: {
    h: 229,
    s: 50,
    l: 42,
    css: "hsl(229, 50%, 42%)",
  },
  Hydrogen: {
    h: 121,
    s: 94,
    l: 50,
    css: "hsl(121, 94%, 50%)",
  },
  Geothermal: {
    h: 47,
    s: 90,
    l: 31,
    css: "hsl(47, 90%, 31%)",
  },
  "Offshore Wind": {
    h: 183,
    s: 94,
    l: 52,
    css: "hsl(183, 94%, 52%)",
  },
  "Onshore Wind": {
    h: 95,
    s: 98,
    l: 36,
    css: "hsl(95, 98%, 36%)",
  },
  "Pumped Hydro": {
    h: 277,
    s: 66,
    l: 56,
    css: "hsl(277, 66%, 56%)",
  },
  "Solar PV": {
    h: 57,
    s: 100,
    l: 50,
    css: "hsl(57, 100%, 50%)",
  },
  "Solar Thermal": {
    h: 30,
    s: 100,
    l: 50,
    css: "hsl(30, 100%, 50%)",
  },
  Nuclear: {
    h: 302,
    s: 90,
    l: 61,
    css: "hsl(302, 90%, 61%)",
  },
  "Coal (Black)": {
    h: 0,
    s: 0,
    l: 0,
    css: "hsl(0, 0%, 0%)",
  },
  "Coal (Brown)": {
    h: 0,
    s: 74,
    l: 9,
    css: "hsl(0, 74%, 9%)",
  },
  Distillate: {
    h: 44,
    s: 77,
    l: 55,
    css: "hsl(44, 77%, 55%)",
  },
  Gas: {
    h: 23,
    s: 95,
    l: 49,
    css: "hsl(23, 95%, 49%)",
  },
  "Data Centre": {
    h: 322,
    s: 58,
    l: 46,
    css: "hsl(322, 58%, 46%)",
  },
};

export type Technology = keyof typeof ProjectTechnologyColors;

export type Country = (typeof countries)[keyof typeof countries];

export type ProjectType = "load" | "generation" | "storage";

export interface Project {
  id: string;
  project_name: string;
  information: string;
  created: string;
  updated: string;
  author: string;
  state: string;
  technology: Technology;
  project_status: ProjectStatus;
  project_type: ProjectType;
  project_size_mw: number;
  storage_size_mwh: number | null;
  turbines: number | null;
  developer: string | null;
  da_status: string | null;
  da_link: string | null;
  da_source: string | null;
  opennem: string | null;
  epbc_code: string | null;
  epbc_link: string | null;
  epbc_status: string | null;
  council_area: string | null;
  exact_point: boolean;
  country: Country | null;
  owner: string | null;
  has_geom: boolean;
  rez: string | null;
  point: [number, number] | null;
  network: string;
}

export enum ProjectStatus {
  Abandoned = "Abandoned",
  Approved = "Approved",
  Cancelled = "Cancelled",
  Concept = "Concept",
  Construction = "Construction",
  Commissioning = "Commissioning",
  Decommissioned = "Decommissioned",
  Development = "Development",
  Operating = "Operating",
  Withdrawn = "Withdrawn",
  NA = "NA",
}

export type ProjectStatusType =
  | "Development"
  | "Abandoned"
  | "Decommissioned"
  | "Operating"
  | "Construction"
  | "Commissioning";

// statuses ordered from beginning to end of project lifecycle, status position used in ui
export const projectStatusTypes: ProjectStatusType[] = [
  "Development",
  "Construction",
  "Commissioning",
  "Operating",
  "Decommissioned",
  "Abandoned",
];

// user-facing technology options (excludes internal "Research" entry)
export const technologyOptions = (
  Object.keys(ProjectTechnologyColors) as Technology[]
).filter((t) => t !== "Research");

export const projectTypeOptions = ["generation", "storage", "load"] as const;

export const daStatusOptions = [
  "Approved",
  "Assessment",
  "Not Approved",
  "Not Submitted",
  "Withdrawn",
] as const;

export const countryOptions = Object.values(countries) as Country[];

export const networkOptions = [
  "Alice Springs Power System",
  "Alinta",
  "Darwin-Katherine Interconnected System",
  "East Kimberley",
  "N/A",
  "NEM",
  "NWIS",
  "NWPS",
  "Northern Goldfields",
  "Off-Grid",
  "Private",
  "SWIS",
  "Southern Cross Energy North",
  "Tennant Creek Power System",
] as const;

export const getStatusType = (
  status: ProjectStatus | null,
): ProjectStatusType => {
  // assume operating with no data
  if (status === null) {
    return "Development";
  }

  switch (status) {
    case ProjectStatus.Abandoned:
    case ProjectStatus.Cancelled:
    case ProjectStatus.Withdrawn:
      return "Abandoned";
    case ProjectStatus.Decommissioned:
      return "Decommissioned";
    case ProjectStatus.Concept:
    case ProjectStatus.Approved:
    case ProjectStatus.Development:
      return "Development";
    case ProjectStatus.Construction:
      return "Construction";
    case ProjectStatus.Commissioning:
      return "Commissioning";
    case ProjectStatus.Operating:
    default:
      return "Operating";
  }
};

export const getStatuses = (statusType: ProjectStatusType): ProjectStatus[] => {
  switch (statusType) {
    case "Abandoned":
      return [
        ProjectStatus.Abandoned,
        ProjectStatus.Cancelled,
        ProjectStatus.Withdrawn,
      ];
    case "Decommissioned":
      return [ProjectStatus.Decommissioned];
    case "Development":
      return [
        ProjectStatus.Concept,
        ProjectStatus.Approved,
        ProjectStatus.Development,
        ProjectStatus.NA,
        null as unknown as ProjectStatus,
      ];
    case "Construction":
      return [ProjectStatus.Construction];
    case "Commissioning":
      return [ProjectStatus.Commissioning];
    case "Operating":
      return [ProjectStatus.Operating];
    default:
      return [];
  }
};

// Deprecated
export interface SearchResultsResponse {
  projects: Project[];
  places: GeocodeSearchResult[];
}

// Deprecated
export interface ProjectLocationSearchResultsResponse {
  projects: string[]; // project uuids
  places: GeocodeSearchResult[];
}

export interface ExtendedSearchResultsResponse {
  projects: string[]; // uuids
  infrastructure: Infrastructure[];
  substations: Substation[];
  organisations: Organisation[];
  places: GeocodeSearchResult[];
}

export interface MLFDataPoint {
  year: string;
  DUID: string;
  MLF: number;
}

export interface PPAOfftaker {
  id: string;
  type: string;
  duration: string | null;
  quantity: number | null;
  commencement: Date | null;
  units: string | null;
  source_id: string;
  target_id: string;
}

export interface ProjectListResponse {
  projects: Project[];
  // the input was valid and all filters were parsed. If false, the input filters were invalid and some were ignored
  valid: boolean;
}

export interface DBProjectMetadata {
  latitude: number;
  longitude: number;
  project_name: string;
  state: string;
  technology: string;
  project_type: string;
  project_size_mw: string;
  storage_size_mwh: string;
  turbines: string;
  developer: string;
  project_status: string;
  da_status: string;
  epbc_code: string;
  epbc_link: string;
  epbc_status: string;
  owner: string;
  opennem: string;
  council_area: string;
  network: string;
  rez: string;
  id: string;
  data_updated_at?: string;
}

export interface ProjectMetadata {
  point: [number, number];
  project_name: string;
  state: string;
  technology: string;
  project_type: string;
  project_size_mw: string;
  storage_size_mwh: string;
  turbines: string;
  developer: string;
  project_status: string;
  da_status: string;
  epbc_code: string;
  epbc_link: string;
  epbc_status: string;
  owner: string;
  opennem: string;
  council_area: string;
  network: string;
  rez: string;
  id: string;
  data_updated_at?: string;
}

export type ProjectMetadataWithFields = WithFields<ProjectMetadata>;

export interface DBTurbinesMetadata {
  longitude: number;
  latitude: number;
  project_id: string;
  project_name: string;
  mw: number;
  oem: string;
  hub_height: number[];
  rotor_diameter: number[];
  rotor_tip_height: number[];
  model: string;
}

export interface TurbinesMetadata {
  point: [number, number];
  project_id: string;
  project_name: string;
  mw: number;
  oem: string;
  hub_height: number[];
  rotor_diameter: number[];
  rotor_tip_height: number[];
  model: string;
}
